import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { getApiBaseUrl } from "../../config";

const baseQuery = fetchBaseQuery({
  baseUrl: getApiBaseUrl(),
  credentials: "include",
  prepareHeaders: (headers, { getState }) => {
    const user = getState().user?.currentUser;
    if (user?.id) {
      headers.set("userid", user.id);
    }
    return headers;
  },
});

// Define our API service for the user table
export const userApi = createApi({
  reducerPath: "userApi",
  baseQuery,
  tagTypes: ["User"],
  endpoints: (builder) => ({
    // Get all users
    getUser: builder.query({
      async queryFn(id, queryApi, extraOptions, baseQuery) {
        try {
          const result = await baseQuery({
            url: `users/${id}`,
            method: "GET",
          });

          if (result.error) {
            console.warn("Failed to fetch user details:", result.error);
            return {
              error: {
                status: result.error.status || 500,
                data: { message: "Failed to fetch user details" },
              },
            };
          }

          return result;
        } catch (error) {
          console.warn("User API error:", error);
          return {
            error: {
              status: 500,
              data: { message: "Failed to fetch user details" },
            },
          };
        }
      },
      providesTags: ["User"],
    }),

    // Admin: get a paginated, server-filtered list of users.
    getUsers: builder.query({
      async queryFn(
        {
          page = 1,
          limit = 20,
          search = "",
          type = "",
          role = "",
          isVerified = undefined,
          invitationStatus = "",
          createdFrom = "",
          createdTo = "",
        } = {},
        queryApi,
        extraOptions,
        baseQuery
      ) {
        try {
          const result = await baseQuery({
            url: "/users",
            method: "GET",
            params: {
              page,
              limit,
              ...(search ? { search } : {}),
              ...(type ? { type } : {}),
              ...(role ? { role } : {}),
              ...(isVerified !== undefined ? { isVerified } : {}),
              ...(invitationStatus ? { invitationStatus } : {}),
              ...(createdFrom ? { createdFrom } : {}),
              ...(createdTo ? { createdTo } : {}),
            },
          });

          if (result.error) {
            console.warn("Failed to fetch users:", result.error);
            return {
              error: {
                status: result.error.status || 500,
                data: { message: "Failed to fetch users" },
              },
            };
          }

          return result;
        } catch (error) {
          console.warn("User API error:", error);
          return {
            error: {
              status: 500,
              data: { message: "Failed to fetch users" },
            },
          };
        }
      },
      providesTags: (result) =>
        result && result.users && Array.isArray(result.users)
          ? [
            ...result.users.map((u) => ({ type: "User", id: u._id || u.id })),
            { type: "User", id: "LIST" },
          ]
          : [{ type: "User", id: "LIST" }],
    }),

    createUser: builder.mutation({
      async queryFn(body, queryApi, extraOptions, baseQuery) {
        try {
          const result = await baseQuery({
            url: "/users",
            method: "POST",
            body,
          });

          if (result.error) {
            console.warn("Failed to create user:", result.error);
            return {
              error: {
                status: result.error.status || 500,
                data: { message: "Failed to create user" },
              },
            };
          }

          return result;
        } catch (error) {
          console.warn("User API error:", error);
          return {
            error: {
              status: 500,
              data: { message: "Failed to create user" },
            },
          };
        }
      },
      invalidatesTags: [{ type: "User", id: "LIST" }],
    }),

    updateUser: builder.mutation({
      async queryFn({ id, ...body }, queryApi, extraOptions, baseQuery) {
        try {
          const result = await baseQuery({
            url: `/users/${id}`,
            method: "PUT",
            body,
          });

          if (result.error) {
            console.warn("Failed to update user:", result.error);
            return {
              error: {
                status: result.error.status || 500,
                data: { message: "Failed to update user" },
              },
            };
          }

          return result;
        } catch (error) {
          console.warn("User API error:", error);
          return {
            error: {
              status: 500,
              data: { message: "Failed to update user" },
            },
          };
        }
      },
      invalidatesTags: (result, error, arg) => [
        { type: "User", id: arg.id },
        { type: "User", id: "LIST" },
      ],
    }),

    deleteUser: builder.mutation({
      async queryFn(id, queryApi, extraOptions, baseQuery) {
        try {
          const result = await baseQuery({
            url: `/users/${id}`,
            method: "DELETE",
          });

          if (result.error) {
            console.warn("Failed to delete user:", result.error);
            return {
              error: {
                status: result.error.status || 500,
                data: { message: "Failed to delete user" },
              },
            };
          }

          return result;
        } catch (error) {
          console.warn("User API error:", error);
          return {
            error: {
              status: 500,
              data: { message: "Failed to delete user" },
            },
          };
        }
      },
      invalidatesTags: (result, error, id) => [
        { type: "User", id },
        { type: "User", id: "LIST" },
      ],
    }),

    // Use invite endpoint - this follows backend `inviteUser` expectations
    inviteUser: builder.mutation({
      async queryFn(body, queryApi, extraOptions, baseQuery) {
        try {
          const result = await baseQuery({
            url: "/auth/invite-user",
            method: "POST",
            body,
          });

          if (result.error) {
            console.warn("Failed to invite user:", result.error);
            return {
              error: {
                status: result.error.status || 500,
                data: { message: "Failed to invite user" },
              },
            };
          }

          return result;
        } catch (error) {
          console.warn("User API error:", error);
          return {
            error: {
              status: 500,
              data: { message: "Failed to invite user" },
            },
          };
        }
      },
      invalidatesTags: [{ type: "User", id: "LIST" }],
    }),

    // Roles list
    getRoles: builder.query({
      async queryFn(arg, queryApi, extraOptions, baseQuery) {
        try {
          const result = await baseQuery({
            url: "/roles",
            method: "GET",
          });

          if (result.error) {
            console.warn("Failed to fetch roles:", result.error);
            return {
              error: {
                status: result.error.status || 500,
                data: { message: "Failed to fetch roles" },
              },
            };
          }

          return result;
        } catch (error) {
          console.warn("User API error:", error);
          return {
            error: {
              status: 500,
              data: { message: "Failed to fetch roles" },
            },
          };
        }
      },
      providesTags: ["User"],
    }),

    // Bulk password reset after maintenance
    bulkPasswordReset: builder.mutation({
      async queryFn(body, queryApi, extraOptions, baseQuery) {
        try {
          const result = await baseQuery({
            url: "/auth/bulk-password-reset-maintenance",
            method: "POST",
            body,
          });

          if (result.error) {
            console.warn("Failed to reset passwords:", result.error);
            return {
              error: {
                status: result.error.status || 500,
                data: { message: "Failed to reset passwords" },
              },
            };
          }

          return result;
        } catch (error) {
          console.warn("User API error:", error);
          return {
            error: {
              status: 500,
              data: { message: "Failed to reset passwords" },
            },
          };
        }
      },
      invalidatesTags: [{ type: "User", id: "LIST" }],
    }),
  }),
});

// Export hooks for usage in components
export const {
  useGetUserQuery,
  useGetUsersQuery,
  useLazyGetUsersQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useInviteUserMutation,
  useGetRolesQuery,
  useBulkPasswordResetMutation,
} = userApi;
